import React, { useEffect, useState } from "react";
import { Input, Button, Modal, Radio, Alert, message, Popconfirm } from "antd";

import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import { useForm, Controller } from "react-hook-form";
import axios from "axios";
import ButtonLoader from "../ButtonLoader";

const schema = yup.object().shape({
  title: yup.string().required("Brand title is required"),
  status: yup.string().required("Status is required"),
});

// `minimal` mode (used for the inline "add new brand" popup from the
// Inventory form) hides the status field/delete action and always defaults
// to Active - matches CityForm.jsx's single-field pattern.
const BrandForm = ({
  isModalOpen,
  editingBrand,
  closeModal,
  handleUpdateList,
  minimal = false,
}) => {
  const [loading, setLoading] = useState(true);
  const [showErrors, setShowErrors] = useState(false);
  const [errorsMessage, setErrorsMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [isDataSet, setIsDataSet] = useState(false);
  const [deletePermission, setDeletePermission] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    context: { isEdit: !!editingBrand },
    defaultValues: { status: "active" },
  });

  const fetchBrand = async (brandId) => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/brands/${brandId}`
      );
      reset({ ...response.data.data });
      setLoading(false);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    setIsDataSet(true);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (isDataSet && editingBrand) {
      fetchBrand(editingBrand.id);
    }
  }, [isDataSet, editingBrand]);

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("userData"));
    if (storedUser) {
      setDeletePermission(parseInt(storedUser.permissions["brands.delete"]) === 1);
    }
  }, []);

  const onSubmitBrand = async (data) => {
    let differFormFields = {};
    if (editingBrand !== null) {
      differFormFields = { id: editingBrand.id };
    }
    const finalFormData = {
      title: data.title.trim(),
      status: data.status,
      ...differFormFields,
    };
    setSubmitting(true);
    const url =
      editingBrand === null
        ? `${process.env.REACT_APP_BASE_URL}/api/brands/create`
        : `${process.env.REACT_APP_BASE_URL}/api/brands/update`;

    try {
      if (editingBrand === null) {
        const response = await axios.post(url, finalFormData);
        message.success("Brand created successfully");
        handleUpdateList(response.data.data.inserted_id, finalFormData.title);
      } else {
        const response = await axios.put(url, finalFormData);
        const updated_row_count = response.data.data.updated_row_count;
        if (updated_row_count === 1) {
          message.success("Brand updated successfully");
        }
        handleUpdateList();
      }
      setSubmitting(false);
      setShowErrors(false);
      closeModal();
    } catch (error) {
      if (error.response && error.response.status > 300) {
        const errorData = error.response.data.message;
        setErrorsMessage(errorData);
        setShowErrors(true);
        setTimeout(() => setShowErrors(false), 3000);
        setSubmitting(false);
      } else {
        console.error("Error:", error);
        setSubmitting(false);
      }
    }
  };

  const handleDelete = async () => {
    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_BASE_URL}/api/brands/${editingBrand.id}`
      );
      const updated_row_count = response.data.data.updated_row_count;
      if (updated_row_count === 1) {
        message.success("Brand deleted");
        handleUpdateList();
        closeModal();
      }
    } catch (err) {
      console.log(err);
    }
  };

  return (
    <Modal
      title={editingBrand ? "Edit Brand" : "Add New Brand"}
      open={isModalOpen}
      onCancel={() => {
        reset();
        closeModal();
      }}
      footer={null}
      maskClosable={false}
    >
      {loading ? (
        <ButtonLoader wrapperClass={`min-h-50`} />
      ) : (
        <>
          {showErrors && (
            <div className="col-sm-12 mb-3">
              <Alert message={errorsMessage} type="error" closeIcon={false} />
            </div>
          )}
          <form onSubmit={handleSubmit(onSubmitBrand)}>
            <div className="mb-3">
              <label className="form-label">
                Brand Title <span className="text-danger">*</span>
              </label>
              <Controller
                name="title"
                control={control}
                render={({ field }) => <Input {...field} />}
              />
              {errors.title && (
                <div className="text-danger small">{errors.title.message}</div>
              )}
            </div>
            {!minimal && (
              <div className="mb-3">
                <label className="form-label me-3">Status</label>
                <Controller
                  name="status"
                  control={control}
                  render={({ field }) => (
                    <Radio.Group {...field}>
                      <Radio value="active">Active</Radio>
                      <Radio value="inactive">Inactive</Radio>
                    </Radio.Group>
                  )}
                />
                {errors.status && (
                  <div className="text-danger small">{errors.status.message}</div>
                )}
              </div>
            )}

            <Button type="primary" htmlType="submit" className="me-2" disabled={submitting}>
              {!submitting ? (
                editingBrand ? "Update Brand" : "Add Brand"
              ) : (
                <ButtonLoader text="Submitting..." />
              )}
            </Button>
            {!minimal && editingBrand && deletePermission && (
              <Popconfirm
                title="Are you sure to delete this brand?"
                onConfirm={handleDelete}
                okText="Yes"
                cancelText="No"
              >
                <Button danger>Delete Brand</Button>
              </Popconfirm>
            )}
          </form>
        </>
      )}
    </Modal>
  );
};

export default BrandForm;
